/**
 * Tax-report settings disclosure — the subset of `configuration_snapshot` that
 * MOVES THE MONEY, and the comparison that says two reports are not alike.
 *
 * THE GAP THIS CLOSES
 * -------------------
 * `bat.tax_reports.configuration_snapshot` records the rules each report ran
 * under, per report. Nothing rendered it. So two 2025/ES reports could sit in
 * the history table showing 4.214,07 € and 4.197,62 € with the table offering
 * only Año / País / Modelo / Fecha / Estado — no column, badge or modal field
 * from which a user could learn that one deducted trading fees and the other
 * did not. The data was already on the wire (the list query selects
 * `configurationSnapshot`, and no response schema strips it); it was simply
 * never read. Nothing here recomputes anything — it reads the stored receipt.
 *
 * WHY ONLY THESE EIGHT KEYS
 * -------------------------
 * A snapshot carries ~34 keys. Most are decoration: they are persisted at the
 * DB-resolved jurisdiction value and NO code in the generation pipeline reads
 * them back, so displaying them would invite a user to "explain" a difference
 * with a key that cannot produce one. The eight below were each traced to a
 * consumer that changes a persisted figure (proceeds, cost basis, gain/loss)
 * — the read side was verified in source, not taken from a doc comment:
 *
 *  - `costMethod`      → tax-report-generation.processor.ts builds
 *                        `calcConfig.costMethod`; cost-basis-engine.ts
 *                        `getStrategy()` picks FIFO/LIFO/HIFO/… from it, which
 *                        selects WHICH lots a disposal consumes.
 *  - `baseCurrency`    → the processor's `resolveReportCurrency` input, i.e.
 *                        `targetCurrency` — the currency every fiat leg is
 *                        computed and stored in.
 *  - `washSaleEnabled` → tax-report-disposal-processing.ts gates
 *                        `applyWashSaleAdjustmentToDisposal`, which overwrites
 *                        `finalGainLoss` / `finalCostBasis` / `finalTaxableGain`.
 *  - `washSaleDays`    → same file: the look-back/forward replacement window.
 *  - `deductTradingFees` / `deductGasFees` / `deductWithdrawalFees` /
 *    `deductMiningStakingExpenses`
 *                      → resolve-fee-deductions.ts turns them into the
 *                        `FeeDeductionConfig` that cost-basis-engine.ts
 *                        `sumFiatDeductibleFees` applies, subtracting the
 *                        matching fee types from each disposal's proceeds.
 *                        This is the pair that produced the 16,45 € gap.
 *
 * DELIBERATELY EXCLUDED, despite reading like tax switches: `compensateIncome`
 * and `compensateLossesGains` are read off the snapshot by calculations.handler.ts
 * but ONLY to set an `isCompensable` flag on a read DTO — no total, proceeds or
 * gain/loss moves, and the engine's own compensation path is bypassed
 * (`compensateIncome` is hardcoded `false` in the processor and the resulting
 * `taxSummary` is destructured away at both call sites). They explain nothing
 * about a differing figure, so they are not offered as an explanation.
 *
 * ⚠️ Confirmed by measurement and acted on: nothing renders `isCompensable`, so the
 * two settings changed nothing a filer could see, and their panel toggles have been
 * removed. Both keys stay on the snapshot (`APPLIED_TAX_REPORT_SETTING_KEYS`) as a
 * receipt of the jurisdiction defaults the report assumed — which is exactly why
 * they must remain excluded HERE: two reports can now only ever differ in them
 * because the DB config changed, never because a user chose differently.
 */

/**
 * The snapshot keys a difference in which genuinely changes a reported figure.
 *
 * ⚠️ Before adding a key: find the code that READS it off the snapshot and
 * branches on its value. A key that is merely persisted, spread, typed or
 * logged is decoration, and offering it as the reason two reports differ is a
 * false explanation on a tax filing.
 */
export const MONEY_AFFECTING_TAX_REPORT_SETTING_KEYS = [
  'costMethod',
  'baseCurrency',
  'washSaleEnabled',
  'washSaleDays',
  'deductTradingFees',
  'deductGasFees',
  'deductWithdrawalFees',
  'deductMiningStakingExpenses',
] as const

export type TaxReportMoneySettingKey = (typeof MONEY_AFFECTING_TAX_REPORT_SETTING_KEYS)[number]

/**
 * The money-affecting settings a single report ran under.
 *
 * Every field is OPTIONAL and absence is meaningful: it means the stored
 * snapshot does not record that key (reports generated before the key existed).
 * Absent must never be rendered as `false`/`0` — "fees were not deducted" is a
 * different claim from "this report does not say", and only one of them is true.
 */
export interface TaxReportMoneySettings {
  costMethod?: string
  baseCurrency?: string
  washSaleEnabled?: boolean
  washSaleDays?: number
  deductTradingFees?: boolean
  deductGasFees?: boolean
  deductWithdrawalFees?: boolean
  deductMiningStakingExpenses?: boolean
}

/**
 * Read the money-affecting settings out of a stored `configuration_snapshot`.
 *
 * A value is kept only when it carries the type the engine actually consumes;
 * a wrong-typed value (e.g. `washSaleDays: "30"`) is DROPPED rather than
 * coerced, because a snapshot the engine could not have read is not evidence
 * of what the engine did. `baseCurrency` is additionally dropped when empty,
 * which is the neutral-baseline value `resolveReportCurrency` treats as "not
 * supplied" — recording it as a currency choice would misattribute a fallback.
 */
export function extractTaxReportMoneySettings(snapshot: unknown): TaxReportMoneySettings {
  if (snapshot === null || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    return {}
  }

  const record = snapshot as Record<string, unknown>
  const settings: TaxReportMoneySettings = {}

  // Per-key reads rather than a loop: each key has its own value type, so an
  // explicit check is what lets the compiler verify the assignment.
  const costMethod = record['costMethod']
  if (typeof costMethod === 'string' && costMethod.length > 0) {
    settings.costMethod = costMethod
  }

  const baseCurrency = record['baseCurrency']
  if (typeof baseCurrency === 'string' && baseCurrency.length > 0) {
    settings.baseCurrency = baseCurrency
  }

  const washSaleEnabled = record['washSaleEnabled']
  if (typeof washSaleEnabled === 'boolean') {
    settings.washSaleEnabled = washSaleEnabled
  }

  const washSaleDays = record['washSaleDays']
  if (typeof washSaleDays === 'number' && Number.isFinite(washSaleDays)) {
    settings.washSaleDays = washSaleDays
  }

  const deductTradingFees = record['deductTradingFees']
  if (typeof deductTradingFees === 'boolean') {
    settings.deductTradingFees = deductTradingFees
  }

  const deductGasFees = record['deductGasFees']
  if (typeof deductGasFees === 'boolean') {
    settings.deductGasFees = deductGasFees
  }

  const deductWithdrawalFees = record['deductWithdrawalFees']
  if (typeof deductWithdrawalFees === 'boolean') {
    settings.deductWithdrawalFees = deductWithdrawalFees
  }

  const deductMiningStakingExpenses = record['deductMiningStakingExpenses']
  if (typeof deductMiningStakingExpenses === 'boolean') {
    settings.deductMiningStakingExpenses = deductMiningStakingExpenses
  }

  return settings
}

/**
 * Which money-affecting settings differ between two reports.
 *
 * Returns the keys in `MONEY_AFFECTING_TAX_REPORT_SETTING_KEYS` order, so the
 * disclosure reads the same everywhere. An empty array means the two reports
 * ran under the same rules — which is exactly the case that must stay silent:
 * a difference indicator that fires on two identical reports teaches the user
 * to ignore it.
 *
 * A key recorded by one report and absent from the other COUNTS as a
 * difference: one of them documents a rule the other does not, and that is
 * itself the thing the user cannot otherwise see.
 */
export function diffTaxReportMoneySettings(
  left: TaxReportMoneySettings,
  right: TaxReportMoneySettings,
): TaxReportMoneySettingKey[] {
  return MONEY_AFFECTING_TAX_REPORT_SETTING_KEYS.filter((key) => left[key] !== right[key])
}
